import { useId } from 'react';

/**
 * A small trend line for a stat tile: one series, 2px line, soft area, a dot on the latest value.
 * Decorative — the tile states the value; `label` describes the trend for screen readers.
 */
export function Sparkline({ values, color, label }: { values: readonly bigint[]; color: string; label?: string }) {
  const gradient = useId();
  if (values.length < 2) return null;
  const numbers = values.map(Number);
  const width = 84;
  const height = 30;
  const pad = 4;
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  const span = max - min || 1;
  const points = numbers.map((value, i) => [pad + (i * (width - pad * 2)) / (numbers.length - 1), pad + (1 - (value - min) / span) * (height - pad * 2)] as const);
  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${points.at(-1)![0].toFixed(1)},${height} L${points[0]![0].toFixed(1)},${height} Z`;
  const [lastX, lastY] = points.at(-1)!;
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} width={width} height={height} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <defs>
        <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradient})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="3.5" fill={color} stroke="var(--color-elevated)" strokeWidth="2" />
    </svg>
  );
}
